import {useCallback, useEffect, useRef} from 'react';

import {useManager} from '@cosmos-kit/react-lite';
import {create} from 'zustand';

import {APP_CONFIG} from 'app-config';
import {CosmosKitAuthProvider} from 'app/services/Authentication/cosmos-kit/CosmosKitAuthProvider';
import {Web3AuthProvider} from 'app/services/Authentication/web3auth/Web3AuthProvider';
import {
  ExtensionNotInstalledError,
  WalletProvider,
  WalletProviderInterface,
} from 'app/types/providers';

import {useNotifications} from './useNotifications';

type State = 'none' | 'initializing' | 'initialized';

type AuthStoreProps = {
  connected: boolean;
  provider: WalletProviderInterface | null;
  providerPreview: WalletProviderInterface | null;
  state: State;
  setProvider: (provider: WalletProviderInterface | null) => void;
  setProviderPreview: (provider: WalletProviderInterface | null) => void;
  setState: (state: State) => void;
  reset: () => void;
};

type WalletProviderClass = new () => WalletProviderInterface;

const PROVIDERS = {
  web3_auth: Web3AuthProvider as unknown as WalletProviderClass,
  cosmos_kit: CosmosKitAuthProvider as unknown as WalletProviderClass,
} as const satisfies Record<WalletProvider, WalletProviderClass>;

type ConnectCallback<T extends WalletProvider> = (
  provider: WalletProvider,
  sessionCheck?: boolean,
  options?: Parameters<InstanceType<(typeof PROVIDERS)[T]>['connect']>[1],
) => Promise<void>;

export const AUTH_STORAGE_KEY = 'auth_provider';
export const TEST_PRIVATE_KEY = 'TEST__private_key';

export const useAuthenticationStore = create<AuthStoreProps>(set => ({
  connected: false,
  state: 'none',
  provider: null,
  providerPreview: null,
  setState: (state: State) => set({state}),
  setProvider: (provider: WalletProviderInterface | null) => {
    if (provider) {
      set({provider, connected: true});
      return;
    }

    set({provider: null, connected: false});
  },
  setProviderPreview: (provider: WalletProviderInterface | null) =>
    set({providerPreview: provider}),
  reset: () => set({provider: null, connected: false, state: 'none'}),
}));

export const useAuthentication = <T extends WalletProvider>() => {
  const connected = useAuthenticationStore(state => state.connected);
  const provider = useAuthenticationStore(state => state.provider);
  const providerPreview = useAuthenticationStore(
    state => state.providerPreview,
  );
  const state = useAuthenticationStore(state => state.state);

  const {connect, disconnect} = useAuthenticationMethods<T>();

  // listen for account changes for wallet providers that support it
  useEffect(() => {
    const changeEvent = provider?.accountChangeEvent;

    if (!changeEvent) return;

    const reconnect = () => {
      const walletProvider = localStorage.getItem(
        AUTH_STORAGE_KEY,
      ) as WalletProvider;

      connect(walletProvider);
    };

    window.addEventListener(changeEvent, reconnect);

    return () => {
      window.removeEventListener(changeEvent, reconnect);
    };
  }, [provider, disconnect, connect]);

  let address = provider?.elysAddress;
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    const debug = searchParams.get('debug');
    const debugAddress = searchParams.get('user_address');
    // Useful to debug specific user related issues
    if (
      debug === 'true' &&
      debugAddress &&
      APP_CONFIG.CHAIN_NETWORK !== 'mainnet'
    ) {
      address = debugAddress;
    }
  }

  return {
    connect,
    disconnect,
    state,
    connected,
    address: address!,
    provider: provider!,
    providerPreview: providerPreview,
  };
};

export const useAuthenticationMethods = <T extends WalletProvider>() => {
  // Temporary fix for jest tests

  const isJest =
    typeof process !== 'undefined' && process.env.JEST_WORKER_ID !== undefined;

  // eslint-disable-next-line
  const manager = !isJest ? useManager() : null!;

  const managerRef = useRef(manager);
  managerRef.current = manager;

  //

  const {notifyError} = useNotifications();
  const setProvider = useAuthenticationStore(state => state.setProvider);
  const setProviderPreview = useAuthenticationStore(
    state => state.setProviderPreview,
  );
  const setState = useAuthenticationStore(state => state.setState);

  const createProvider = useCallback(
    async <W extends WalletProvider>(provider: W) => {
      const instance = new PROVIDERS[provider]();

      await instance.init();

      return instance as InstanceType<(typeof PROVIDERS)[W]>;
    },
    [],
  );

  const connect = useCallback<ConnectCallback<T>>(
    async (walletProvider, sessionCheck, options) => {
      try {
        const provider = await createProvider(walletProvider);

        setProviderPreview(provider);

        await provider.connect(
          ...[
            sessionCheck,
            managerRef.current
              ? {
                  ...(options as Record<string, unknown>),
                  manager: managerRef.current,
                }
              : options,
          ],
        );

        setProviderPreview(null);

        if (!provider.connected) {
          return;
        }

        setProvider(provider);
        localStorage.setItem(AUTH_STORAGE_KEY, walletProvider);
      } catch (error) {
        setProviderPreview(null);

        // prevent showing an error message if the user has already connected
        if (useAuthenticationStore.getState().connected) return;

        if (error instanceof ExtensionNotInstalledError) {
          window.open(error.url, '_blank');
        }

        if (sessionCheck) {
          return;
        }

        if (error instanceof Error) {
          notifyError(error.message);
        }

        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    },
    [createProvider, setProvider, setProviderPreview, notifyError],
  );

  const disconnect = useCallback(async () => {
    const provider = useAuthenticationStore.getState().provider;
    const providerPreview = useAuthenticationStore.getState().providerPreview;

    if (!provider && !providerPreview) return;

    await provider?.disconnect();

    setProvider(null);
    setProviderPreview(null);
    setState('initialized');

    if (provider) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, [setProvider, setProviderPreview, setState]);

  const init = useCallback(async () => {
    const isFirstRender = useAuthenticationStore.getState().state === 'none';

    if (!isFirstRender) return;

    setState('initializing');

    const walletProvider = localStorage.getItem(AUTH_STORAGE_KEY) as
      | WalletProvider
      | undefined;

    if (!walletProvider) {
      setState('initialized');
      return;
    }

    await connect(walletProvider, true);

    setState('initialized');
  }, [setState, connect]);

  return {
    init,
    connect,
    disconnect,
  };
};

export const useCosmosKitManager = () => {
  const isJest =
    typeof process !== 'undefined' && process.env.JEST_WORKER_ID !== undefined;

  // eslint-disable-next-line
  return !isJest ? useManager() : null!;
};
